import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ColumnDef } from '@tanstack/react-table';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefundsFiltersPanel } from './RefundsFiltersPanel';

interface FiltersSectionProps {
  searchTerm: string;
  onSearch: (value: string) => void;
  shopId: string;
  onShopChange: (value: string) => void;
  dateFrom: string;
  dateTo: string;
  onDateRangeChange: (from: string, to: string) => void;
  onClearFilters: () => void;
  allColumns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
}

export const FiltersSection = ({
  searchTerm,
  onSearch,
  shopId,
  onShopChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  onClearFilters,
  allColumns,
  selectedColumns,
  onColumnsChange,
}: FiltersSectionProps) => {
  const { t } = useTranslation('refundsModule');
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const activeFiltersCount = useMemo(
    () => [shopId, dateFrom || dateTo].filter(Boolean).length,
    [shopId, dateFrom, dateTo],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = () => {
    onSearch(inputValue.trim());
  };

  return (
    <>
      <Search
        placeholder={t('filters.ref_placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="refunds-search"
        inputDataTestId="refunds-search-input"
      />

      <FiltersDropdown
        title={t('filters.title')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="refunds-filters-toggle"
      >
        <RefundsFiltersPanel
          shopId={shopId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onShopChange={onShopChange}
          onDateRangeChange={onDateRangeChange}
          onClear={onClearFilters}
        />
      </FiltersDropdown>

      <ColumnSelector
        columns={allColumns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={['order_ref']}
        storageKey="refunds-list"
        dataTestId="refunds-column-selector"
      />
    </>
  );
};
